import { ChevronDown } from "@untitledui/icons";
import { type ReactNode, useCallback, useId, useState } from "react";
import { Label } from "@/components/base/input/label";
import { Command, CommandInput } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cx } from "@/utils/cx";
import { filterRiskOptionGroups, type RiskOption, type RiskOptionGroup, RiskOptionList } from "./risk-option-list";

type RiskSelectProps = {
  groups: RiskOptionGroup<RiskOption>[];
  value: string | undefined;
  onChange: (key: string) => void;
  disabledKeys?: string[];
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  isInvalid?: boolean;
  disabled?: boolean;
  /** Shown in place of each option's group icon. */
  getIndicator?: (item: RiskOption) => ReactNode;
  /** Sits over the left edge of the field, outside its button, so it can carry its own tooltip. */
  leadingAdornment?: ReactNode;
};

export const RiskSelect = ({
  groups,
  value,
  onChange,
  disabledKeys,
  label,
  placeholder = "Select risk",
  isRequired,
  isInvalid,
  disabled,
  getIndicator,
  leadingAdornment,
}: RiskSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const labelId = useId();
  const valueId = useId();

  const selected = groups.flatMap((group) => group.items).find((item) => item.key === value);

  const isSelected = useCallback((item: RiskOption) => item.key === value, [value]);
  const isDisabled = useCallback((item: RiskOption) => !!disabledKeys?.includes(item.key), [disabledKeys]);

  const filteredGroups = filterRiskOptionGroups(groups, search);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) setSearch("");
  }, []);

  const handleSelect = useCallback(
    (item: RiskOption) => {
      onChange(item.key);
      handleOpenChange(false);
    },
    [onChange, handleOpenChange],
  );

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <Label id={labelId} isRequired={isRequired}>
          {label}
        </Label>
      )}
      <div className="relative">
        {leadingAdornment && (
          <span className="absolute top-1/2 left-3 z-10 flex -translate-y-1/2">{leadingAdornment}</span>
        )}
        <Popover open={open} onOpenChange={handleOpenChange} modal>
          <PopoverTrigger
            type="button"
            disabled={disabled}
            aria-labelledby={label ? `${labelId} ${valueId}` : undefined}
            aria-invalid={isInvalid || undefined}
            className={cx(
              "flex w-full cursor-pointer items-center gap-2 rounded-lg bg-primary px-3 py-2 text-left outline-hidden ring-1 ring-primary ring-inset transition duration-100 ease-linear",
              open && "ring-2 ring-brand",
              leadingAdornment && "pl-7",
              isInvalid && !open && "ring-error_subtle",
              disabled && "cursor-not-allowed bg-disabled_subtle",
            )}
          >
            <span
              id={valueId}
              className={cx(
                "flex-1 truncate text-sm",
                selected ? "font-medium text-primary" : "text-placeholder",
                disabled && "text-disabled",
              )}
            >
              {selected?.name ?? placeholder}
            </span>
            <ChevronDown aria-hidden="true" className="size-4 shrink-0 stroke-[2.5px] text-fg-quaternary" />
          </PopoverTrigger>
          <PopoverContent align="start" className="p-0 w-[var(--radix-popover-trigger-width)]">
            <Command shouldFilter={false} defaultValue={value}>
              <CommandInput placeholder="Search risk" value={search} onValueChange={setSearch} />
              <RiskOptionList
                groups={filteredGroups}
                isSelected={isSelected}
                isDisabled={isDisabled}
                getIndicator={getIndicator}
                onSelect={handleSelect}
              />
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
